import { MAP_IDS, mapName, TILE_SIZE } from '@gardener/content';
import {
  MAP_NOTES,
  MAP_SHOTS,
  type MapNoteStatus,
  type MapNotesView,
  type MapNoteView,
  type MapStroke,
  type MapTiles,
  type MapTilesIndexView,
} from '@gardener/protocol';
import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  on,
  onCleanup,
  onMount,
  Show,
  untrack,
} from 'solid-js';
import { blob, call } from './api';
import { MapShoot } from './map-shoot';
import {
  densityOf,
  fit,
  levelFor,
  playerView,
  simplify,
  tilesInView,
  toScreen,
  toWorld,
  type View,
  zoomAt,
} from './map-view';
import { go, route } from './nav';
import {
  ago,
  ask,
  EmptyState,
  Icon,
  IconButton,
  LoadingLine,
  optimistic,
  SETTINGS_INPUT,
  Segmented,
  TOOLBAR_CONTROL,
  TOOLBAR_ICON_CONTROL,
  Toolbar,
  toast,
  when,
} from './ui';

/** The pen's width on screen (CSS px): a note's strokes are thinner in the world the closer one draws them. */
const PEN = 6;
/** A note still being worked on (the Open list). */
const ACTIVE: readonly MapNoteStatus[] = ['open', 'question', 'fixed'];
/** Each status as it reads, its ink on the map and its badge. */
const STATUS: Record<MapNoteStatus, [string, string, 'error' | 'warning' | 'success' | 'neutral' | 'outline']> = {
  open: ['Open', '#ff2bd6', 'error'],
  question: ['Question', '#f5a524', 'warning'],
  fixed: ['Fixed', '#2bd9a0', 'success'],
  done: ['Done', '#8b8f98', 'neutral'],
  wont_fix: ['Won’t fix', '#8b8f98', 'outline'],
};

/**
 * A map's tiles (ADR-0223, protocol `MapTilesIndexView`, `MapTiles`) from the server, which reads them from the bucket
 * this page's Shoot fills (in dev, `.maptiles/`); null for none.
 */
const tilesOf = <T,>(path: string): Promise<T | null> => call<T>('GET', `/api/admin/${path}`).catch(() => null);

/** Tiles asked for so far, by sha: null while one comes. */
const images = new Map<string, ImageBitmap | null>();

/**
 * Maps (ADR-0223): a map as the game draws it, zoomed from the whole island down to its art's own px and past them, and
 * the owner's notes on it: drawn with the pen, a line of text each, answered by the agent (the `map-notes` skill) and
 * closed here once its fix holds. `#maps/<map>/<note>`.
 */
export function Maps() {
  const parts = () => route().arg.split('/');
  const mapId = () => (MAP_IDS.includes(parts()[0] ?? '') ? (parts()[0] as string) : 'village');
  const selectedId = () => Number(parts()[1]) || null;
  const open = (map: string, note: number | null = null) => go('maps', note ? `${map}/${note}` : map);

  /** The maps Shoot shoots, each one's versions shot and the one the server's build has. */
  const [indexes, { refetch: reindex }] = createResource(async () => {
    const all = await Promise.all(
      Object.keys(MAP_SHOTS).map(async (id) => [id, await tilesOf<MapTilesIndexView>(`map-tiles/${id}`)] as const),
    );
    return new Map(all.filter((e): e is readonly [string, MapTilesIndexView] => !!e[1]));
  });
  const versions = () => indexes()?.get(mapId())?.versions ?? [];
  /** The version the server's build has: what Shoot shoots. */
  const now = () => indexes()?.get(mapId())?.now ?? null;
  /** The map being shot again (its card over the map), null: none. */
  const [shooting, setShooting] = createSignal<string | null>(null);
  const latest = () => versions().at(-1)?.version ?? null;
  /** The version shown (a note's, its fix's), null: the newest. */
  const [pinned, setPinned] = createSignal<string | null>(null);
  createEffect(on(mapId, () => setPinned(null)));
  const version = () => pinned() ?? latest();
  createEffect(on(selectedId, () => setPinned(null), { defer: true }));
  const [tiles] = createResource(
    () => (version() ? `${mapId()}/${version()}` : null),
    (path) => tilesOf<MapTiles>(`map-tiles/${path}`),
  );
  const hasTiles = (v: string | null) => !!v && versions().some((x) => x.version === v);

  const [notes, { mutate, refetch }] = createResource(mapId, (m) =>
    call<MapNotesView>('GET', `/api/admin/map-notes?map=${m}`),
  );
  const all = () => (notes.error ? [] : (notes()?.notes ?? []));
  const selected = () => all().find((n) => n.id === selectedId()) ?? null;
  const [filter, setFilter] = createSignal<'open' | 'closed' | 'all'>('open');
  const listed = () =>
    all()
      .filter((n) => filter() === 'all' || ACTIVE.includes(n.status) === (filter() === 'open'))
      .reverse();
  const replace = (n: MapNoteView) => mutate((v) => v && { notes: v.notes.map((x) => (x.id === n.id ? n : x)) });

  const [draft, setDraft] = createSignal<MapStroke[]>([]);
  const [text, setText] = createSignal('');
  /** Strokes drawn on: the version the draft is on (a draft goes when the map or its version does). */
  createEffect(on(version, () => setDraft([])));

  async function save() {
    const v = version();
    if (!v || !draft().length || !text().trim()) return;
    try {
      const { note } = await call<{ note: MapNoteView }>('POST', '/api/admin/map-notes', {
        map: mapId(),
        version: v,
        strokes: draft(),
        text: text().trim(),
      });
      mutate((x) => ({ notes: [...(x?.notes ?? []), note] }));
      setDraft([]);
      setText('');
      setFilter('open');
      open(mapId(), note.id);
      toast(`M${note.id} saved`);
    } catch (err) {
      toast((err as Error).message, 'error');
    }
  }

  async function patch(n: MapNoteView, body: { status?: MapNoteStatus; reply?: string }) {
    return optimistic(
      () => {
        replace({ ...n, status: body.status ?? n.status });
        return () => replace(n);
      },
      () => call<{ note: MapNoteView }>('PATCH', `/api/admin/map-notes/${n.id}`, body),
      ({ note }) => replace(note),
    );
  }

  async function remove(n: MapNoteView) {
    const sure = await ask({
      title: `Delete M${n.id}?`,
      body: 'Its drawing, its text and its thread go.',
      confirm: 'Delete',
      danger: true,
    });
    if (sure === null) return;
    await optimistic(
      () => {
        mutate((v) => v && { notes: v.notes.filter((x) => x.id !== n.id) });
        open(mapId());
        return () => mutate((v) => v && { notes: [...v.notes, n].sort((a, b) => a.id - b.id) });
      },
      () => call('DELETE', `/api/admin/map-notes/${n.id}`),
      () => toast(`M${n.id} deleted`),
    );
  }

  const shown = () => [...(indexes()?.keys() ?? [])];
  const shoot = () => {
    if (now()) setShooting(mapId());
  };
  /** The map's newest shot: whole in the bucket (its manifest goes up last), so a new shot skips the tiles it shares. */
  const [known] = createResource(shooting, (m) => {
    const v = indexes()?.get(m)?.versions.at(-1)?.version;
    return v ? tilesOf<MapTiles>(`map-tiles/${m}/${v}`) : null;
  });
  return (
    <div class="flex min-h-0 w-full flex-1">
      <Toolbar>
        <Show when={shown().length > 1}>
          {/* One pick of many maps: a select, so the breadcrumbs keep their room. */}
          <select
            class="ld-select w-40 rounded-full"
            data-size="sm"
            aria-label="Map"
            value={mapId()}
            onChange={(e) => open(e.currentTarget.value)}
          >
            <For each={shown()}>{(id) => <option value={id}>{mapName(id)}</option>}</For>
          </select>
        </Show>
        <Show when={now()}>
          {(v) => (
            <button
              type="button"
              class={TOOLBAR_CONTROL}
              disabled={shooting() !== null}
              title={
                hasTiles(v())
                  ? `Shoot ${mapName(mapId())} again in the game’s client (${v().slice(0, 7)})`
                  : `The server’s ${mapName(mapId())} is ${v().slice(0, 7)}, not shot yet`
              }
              onClick={shoot}
            >
              <Icon name="camera" class="size-3.5" />
              Shoot
              <Show when={!hasTiles(v())}>
                <span class="size-1.5 rounded-full bg-[#f5a524]" role="img" aria-label="not shot yet" />
              </Show>
            </button>
          )}
        </Show>
        <button
          type="button"
          class={TOOLBAR_ICON_CONTROL}
          title="Refresh notes"
          aria-label="Refresh notes"
          onClick={() => refetch()}
        >
          <Icon name="refresh" class={`size-3.5 ${notes.loading ? 'animate-spin' : ''}`} />
        </button>
      </Toolbar>
      <div class="bg-surface-root-bg relative min-w-0 flex-1 overflow-hidden">
        <Show
          when={tiles()}
          fallback={
            <div class="flex h-full items-center justify-center">
              <Show when={!indexes.loading && !tiles.loading} fallback={<LoadingLine label="Loading the map" />}>
                <div class="flex flex-col items-center gap-4">
                  <EmptyState
                    centered
                    title={`No tiles of ${mapName(mapId())} yet`}
                    description="Shoot it: the game’s client draws it in this browser, about a minute, and its tiles go up to the server."
                  />
                  <Show when={now() && !shooting()}>
                    <button type="button" class="ld-button" data-variant="primary" data-size="sm" onClick={shoot}>
                      Shoot {mapName(mapId())}
                    </button>
                  </Show>
                </div>
              </Show>
            </div>
          }
        >
          {(m) => (
            <MapCanvas
              tiles={m()}
              notes={all()}
              selected={selected()}
              draft={draft()}
              select={(id) => open(mapId(), id)}
              stroke={(s) => setDraft([...draft(), s])}
              undo={() => setDraft(draft().slice(0, -1))}
            />
          )}
        </Show>
        <Show when={shooting() && !known.loading && now()}>
          <MapShoot
            map={shooting() as string}
            now={indexes()?.get(shooting() as string)?.now ?? ''}
            known={known() ?? null}
            close={() => setShooting(null)}
            done={(index, fresh, all) => {
              const m = shooting() as string;
              setShooting(null);
              setPinned(null);
              void reindex();
              toast(`${mapName(m)} ${index.now.slice(0, 7)} shot: ${all} tiles, ${fresh} new`);
            }}
          />
        </Show>
        <Show when={draft().length > 0}>
          <form
            class="bg-surface-elevated-bg border-surface-elevated-border absolute bottom-4 left-1/2 flex w-[min(34rem,calc(100%-2rem))] -translate-x-1/2 flex-col gap-2 rounded-xl border p-2 shadow-lg"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            <textarea
              rows={3}
              class={`${SETTINGS_INPUT} block h-auto w-full resize-none py-1.5 leading-6`}
              data-size="sm"
              placeholder="What’s wrong here?"
              maxLength={MAP_NOTES.maxText}
              value={text()}
              ref={(el) => queueMicrotask(() => el.focus())}
              onInput={(e) => setText(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void save();
              }}
            />
            <div class="flex items-center gap-2">
              <span class="text-fg-muted flex-1 truncate px-1 text-xs">
                {draft().length} {draft().length === 1 ? 'stroke' : 'strokes'} · keep drawing to add more · ⌘Z takes one
                back
              </span>
              <button
                type="button"
                class="ld-button"
                data-size="sm"
                onClick={() => {
                  setDraft([]);
                  setText('');
                }}
              >
                Discard
              </button>
              <button type="submit" class="ld-button" data-variant="primary" data-size="sm" disabled={!text().trim()}>
                Save note
              </button>
            </div>
          </form>
        </Show>
      </div>
      <aside class="edge-l flex w-88 shrink-0 flex-col max-md:hidden">
        <Show
          when={selected()}
          fallback={
            <NoteList
              notes={listed()}
              loading={notes.loading}
              error={notes.error ? (notes.error as Error).message : null}
              counts={{
                open: all().filter((n) => ACTIVE.includes(n.status)).length,
                closed: all().filter((n) => !ACTIVE.includes(n.status)).length,
              }}
              filter={filter()}
              setFilter={setFilter}
              version={version()}
              select={(id) => open(mapId(), id)}
            />
          }
        >
          {(n) => (
            <Thread
              n={n()}
              version={version()}
              latest={latest()}
              hasTiles={hasTiles}
              show={setPinned}
              back={() => {
                setPinned(null);
                open(mapId());
              }}
              patch={(body) => patch(n(), body)}
              remove={() => void remove(n())}
            />
          )}
        </Show>
      </aside>
    </div>
  );
}

/** The notes on the map, newest first: each its status, its number and the start of what it says. */
function NoteList(props: {
  notes: MapNoteView[];
  loading: boolean;
  error: string | null;
  counts: { open: number; closed: number };
  filter: 'open' | 'closed' | 'all';
  setFilter: (f: 'open' | 'closed' | 'all') => void;
  version: string | null;
  select: (id: number) => void;
}) {
  return (
    <>
      <div class="edge-b flex h-12 shrink-0 items-center justify-between gap-2 px-3">
        <h2 class="text-[13px] font-medium">Notes</h2>
        <Segmented
          size="xs"
          value={props.filter}
          options={[
            ['open', `Open ${props.counts.open}`],
            ['closed', `Closed ${props.counts.closed}`],
            ['all', 'All'],
          ]}
          onChange={props.setFilter}
        />
      </div>
      <div class="flex min-h-0 flex-1 flex-col overflow-y-auto p-2">
        <Show when={props.error}>
          <div class="text-fg-muted flex items-center gap-2 px-2 py-2 text-[13px]" role="alert">
            <Icon name="danger" class="size-4 shrink-0" />
            {props.error}
          </div>
        </Show>
        <For
          each={props.notes}
          fallback={
            <Show
              when={!props.loading}
              fallback={
                <div class="px-2 py-2">
                  <LoadingLine label="Loading notes" />
                </div>
              }
            >
              <EmptyState
                centered
                title={props.filter === 'open' ? 'Nothing open' : 'No notes'}
                description="Drag on the map with the left button round what you don’t like, and say why."
              />
            </Show>
          }
        >
          {(n) => (
            <button
              type="button"
              class="hover:bg-surface-50-hover-bg focus-visible:outline-brand flex w-full cursor-pointer flex-col gap-1 rounded-lg px-2.5 py-2 text-left focus-visible:outline-2"
              onClick={() => props.select(n.id)}
            >
              <span class="flex items-center gap-2 text-xs">
                <span
                  class="size-2 shrink-0 rounded-full"
                  style={{ background: STATUS[n.status][1] }}
                  aria-hidden="true"
                />
                <span class="font-mono font-medium">M{n.id}</span>
                <span class="text-fg-muted">{STATUS[n.status][0]}</span>
                <Show when={props.version && n.version !== props.version}>
                  <span
                    class="ld-badge"
                    data-variant="outline"
                    data-size="sm"
                    title="Drawn on an older shot of the map"
                  >
                    older map
                  </span>
                </Show>
                <time class="text-fg-muted ml-auto" datetime={n.updatedAt} title={when(n.updatedAt)}>
                  {ago(n.updatedAt)}
                </time>
              </span>
              <span class="text-fg-secondary line-clamp-2 text-[13px] text-pretty">{n.text}</span>
            </button>
          )}
        </For>
      </div>
    </>
  );
}

/**
 * One note: what was written, its thread with the agent, which shot of the map is under it (as drawn, its fix, now), and
 * what the owner does next: answer, accept, reopen, decline, delete.
 */
function Thread(props: {
  n: MapNoteView;
  version: string | null;
  latest: string | null;
  hasTiles: (v: string | null) => boolean;
  show: (v: string | null) => void;
  back: () => void;
  patch: (body: { status?: MapNoteStatus; reply?: string }) => Promise<boolean>;
  remove: () => void;
}) {
  const [reply, setReply] = createSignal('');
  const n = () => props.n;
  const send = async (status: MapNoteStatus | undefined) => {
    const r = reply().trim();
    if (await props.patch({ ...(status ? { status } : {}), ...(r ? { reply: r } : {}) })) setReply('');
  };
  /** The shots worth comparing under it: as drawn, its fix's, the newest. */
  const shots = createMemo(() => {
    const out: [string, string][] = [];
    if (props.hasTiles(n().version)) out.push([n().version, 'As drawn']);
    const fix = n().fixedIn;
    if (fix && fix !== n().version && props.hasTiles(fix)) out.push([fix, 'Fixed']);
    if (props.latest && !out.some(([v]) => v === props.latest)) out.push([props.latest, 'Now']);
    return out;
  });
  return (
    <>
      <div class="edge-b flex h-12 shrink-0 items-center gap-2 px-2">
        <IconButton label="All notes" onClick={props.back}>
          <Icon name="back" class="size-4" />
        </IconButton>
        <span class="font-mono text-[13px] font-medium">M{n().id}</span>
        <span class="ld-badge" data-variant={STATUS[n().status][2]} data-size="sm">
          {STATUS[n().status][0]}
        </span>
        <span class="flex-1" />
        <IconButton label="Delete" danger onClick={props.remove}>
          <Icon name="trash" class="size-4" />
        </IconButton>
      </div>
      <div class="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
        <p class="text-sm leading-6 text-pretty whitespace-pre-wrap">{n().text}</p>
        <p class="text-fg-muted text-xs">
          {n().author?.name ?? 'The owner'} · <time title={when(n().createdAt)}>{ago(n().createdAt)}</time> · cells{' '}
          {n().box.cx}–{n().box.cx + n().box.w - 1} × {n().box.cy}–{n().box.cy + n().box.h - 1} · drawn on{' '}
          <span class="font-mono">{n().version.slice(0, 7)}</span>
        </p>
        <Show when={shots().length > 1}>
          <Segmented
            size="xs"
            value={props.version ?? ''}
            options={shots()}
            onChange={(v) => props.show(v === props.latest ? null : v)}
          />
        </Show>
        <ol class="flex flex-col gap-2">
          <For each={n().history}>
            {(h) => (
              <li
                class={`flex flex-col gap-1 rounded-lg px-2.5 py-2 text-[13px] ${h.by === 'agent' ? 'bg-surface-100-bg' : 'edge'}`}
              >
                <span class="text-fg-muted flex items-center gap-2 text-xs">
                  <span class="text-fg-secondary font-medium">{h.by === 'agent' ? 'Clanker' : 'You'}</span>
                  <Show when={h.status}>{(s) => <span>→ {STATUS[s()][0]}</span>}</Show>
                  <time class="ml-auto" title={when(h.at)}>
                    {ago(h.at)}
                  </time>
                </span>
                <Show when={h.reply}>
                  <span class="text-pretty whitespace-pre-wrap">{h.reply}</span>
                </Show>
                <Show when={h.commits?.length || h.fixedIn}>
                  <span class="text-fg-tertiary font-mono text-xs">
                    {(h.commits ?? []).map((c) => c.slice(0, 7)).join(' ')}
                    {h.fixedIn ? ` · map ${h.fixedIn.slice(0, 7)}` : ''}
                  </span>
                </Show>
              </li>
            )}
          </For>
        </ol>
      </div>
      <div class="edge-t flex shrink-0 flex-col gap-2 p-2">
        <textarea
          rows={2}
          class={`${SETTINGS_INPUT} block h-auto w-full resize-none py-1.5 leading-6`}
          data-size="sm"
          placeholder={n().status === 'question' ? 'Your answer' : 'A reply (optional)'}
          maxLength={MAP_NOTES.maxReply}
          value={reply()}
          onInput={(e) => setReply(e.currentTarget.value)}
        />
        <div class="flex flex-wrap justify-end gap-2">
          <Show when={n().status === 'open'}>
            <button type="button" class="ld-button" data-size="sm" onClick={() => void send('wont_fix')}>
              Won’t fix
            </button>
            <button
              type="button"
              class="ld-button"
              data-size="sm"
              disabled={!reply().trim()}
              onClick={() => void send(undefined)}
            >
              Reply
            </button>
          </Show>
          <Show when={n().status === 'question'}>
            <button
              type="button"
              class="ld-button"
              data-variant="primary"
              data-size="sm"
              disabled={!reply().trim()}
              onClick={() => void send('open')}
            >
              Answer
            </button>
          </Show>
          <Show when={n().status === 'fixed'}>
            <button
              type="button"
              class="ld-button"
              data-size="sm"
              disabled={!reply().trim()}
              onClick={() => void send('open')}
            >
              Reopen
            </button>
            <button
              type="button"
              class="ld-button"
              data-variant="primary"
              data-size="sm"
              onClick={() => void send('done')}
            >
              Looks good
            </button>
          </Show>
          <Show when={n().status === 'done' || n().status === 'wont_fix'}>
            <button type="button" class="ld-button" data-size="sm" onClick={() => void send('open')}>
              Reopen
            </button>
          </Show>
        </div>
      </div>
    </>
  );
}

/**
 * The map itself: its tiles at the level the zoom needs (the coarser ones under them while those load; past the art's
 * own px, its px as squares), the notes' strokes over it in their status' ink, the draft's in the pen's. A left drag
 * draws, a right drag moves (the middle button too, or a left one with Space held), the wheel zooms about the cursor, a
 * click on a note opens it (the legend in the corner says so). Keys: 0 the whole map, 1 a player's screen, ⌘Z takes the
 * draft's last stroke back.
 */
function MapCanvas(props: {
  tiles: MapTiles;
  notes: MapNoteView[];
  selected: MapNoteView | null;
  draft: MapStroke[];
  select: (id: number | null) => void;
  stroke: (s: MapStroke) => void;
  undo: () => void;
}) {
  let box!: HTMLDivElement;
  let canvas!: HTMLCanvasElement;
  const [size, setSize] = createSignal({ w: 1, h: 1 });
  const [view, setShown] = createSignal<View | null>(null);
  /** The whole map, as it opened, until the view is moved: a resize fits it again. */
  let whole = true;
  const setView = (v: View | null) => {
    whole = false;
    setShown(v);
  };
  const fitWhole = () => {
    whole = true;
    setShown(fit(m(), untrack(size).w, untrack(size).h));
  };
  const [cursor, setCursor] = createSignal<{ x: number; y: number } | null>(null);
  const [drawing, setDrawing] = createSignal<[number, number][] | null>(null);
  const [panning, setPanning] = createSignal(false);
  const [space, setSpace] = createSignal(false);
  const [tick, setTick] = createSignal(0);
  const m = () => props.tiles;
  const minScale = () => fit(m(), size().w, size().h).scale * 0.5;

  // A new map: the whole of it, once the canvas has its size. A new version of the same map keeps the view.
  createEffect(
    on(
      () => props.tiles.map,
      () => {
        whole = true;
        setShown(null);
      },
      { defer: true },
    ),
  );
  createEffect(() => {
    const { w, h } = size();
    if (w > 1 && (whole || !untrack(view))) setShown(fit(untrack(m), w, h));
  });
  // A note opened: the view goes to it, unless it is in sight already.
  createEffect(
    on(
      () => [props.selected?.id, view() !== null],
      () => {
        const n = props.selected;
        const v = untrack(view);
        const { w, h } = untrack(size);
        if (!n || !v) return;
        const [x0, y0] = [n.box.cx * TILE_SIZE, n.box.cy * TILE_SIZE];
        const [bw, bh] = [n.box.w * TILE_SIZE, n.box.h * TILE_SIZE];
        const a = toScreen(v, w, h, x0, y0);
        const b = toScreen(v, w, h, x0 + bw, y0 + bh);
        if (a.x >= 0 && a.y >= 0 && b.x <= w && b.y <= h && Math.max(b.x - a.x, b.y - a.y) > 160) return;
        const scale = Math.max(minScale(), Math.min(4, w / (bw * 2.5), h / (bh * 2.5)));
        setView({ x: x0 + bw / 2, y: y0 + bh / 2, scale });
      },
    ),
  );

  onMount(() => {
    const ro = new ResizeObserver(([e]) => {
      const r = e?.contentRect;
      if (!r) return;
      setSize({ w: r.width, h: r.height });
    });
    ro.observe(box);
    const typing = (e: KeyboardEvent) =>
      e.target instanceof HTMLElement && /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName);
    const down = (e: KeyboardEvent) => {
      if (typing(e) || document.querySelector('dialog[open]')) return;
      const v = view();
      const { w, h } = size();
      if (e.key === ' ') {
        e.preventDefault();
        setSpace(true);
      } else if (e.key === '0') fitWhole();
      else if (e.key === '1' && v) setView(playerView(v.x, v.y, w, h));
      else if (e.key === 'z' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        props.undo();
      } else if (e.key === 'Escape' && props.selected) props.select(null);
    };
    const up = (e: KeyboardEvent) => e.key === ' ' && setSpace(false);
    addEventListener('keydown', down);
    addEventListener('keyup', up);
    // The wheel zooms the map, never the page (a listener that may cancel it).
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      const v = view();
      if (!v) return;
      const r = canvas.getBoundingClientRect();
      const factor = Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0015));
      setView(zoomAt(v, r.width, r.height, e.clientX - r.left, e.clientY - r.top, factor, minScale()));
    };
    canvas.addEventListener('wheel', wheel, { passive: false });
    onCleanup(() => {
      ro.disconnect();
      removeEventListener('keydown', down);
      removeEventListener('keyup', up);
      canvas.removeEventListener('wheel', wheel);
    });
  });

  /** Asks for a tile: drawn once it is in. */
  const image = (sha: string) => {
    if (!images.has(sha)) {
      images.set(sha, null);
      blob(`/api/admin/map-tile/${sha}`)
        .then((b) => createImageBitmap(b))
        .then((bmp) => {
          images.set(sha, bmp);
          setTick((t) => t + 1);
        })
        // A tile that fails (the server restarting, the network) is asked for again a little later.
        .catch(() =>
          setTimeout(() => {
            images.delete(sha);
            setTick((t) => t + 1);
          }, 2000),
        );
    }
    return images.get(sha) ?? null;
  };

  // One frame a change: the view, the size, a tile in, the notes, the pen.
  let frame = 0;
  createEffect(() => {
    void [view(), size(), tick(), props.notes, props.selected, props.draft, drawing(), m()];
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(draw);
  });
  onCleanup(() => cancelAnimationFrame(frame));

  function draw() {
    const v = view();
    const { w, h } = size();
    const ctx = canvas.getContext('2d');
    if (!v || !ctx) return;
    const dpr = devicePixelRatio || 1;
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const px = v.scale * dpr;
    const top = levelFor(m(), px);
    // Coarser levels first: what isn't in yet shows blurred, never as a hole.
    for (let z = 0; z <= top; z++) {
      const { x0, y0, x1, y1, span } = tilesInView(m(), z, v, w, h);
      ctx.imageSmoothingEnabled = densityOf(m(), z) > px;
      for (let ty = y0; ty < y1; ty++)
        for (let tx = x0; tx < x1; tx++) {
          const sha = m().tiles[`${z}/${tx}/${ty}`];
          if (!sha) continue;
          // Only the level wanted is fetched (and the whole map's one tile); the others draw if they are in.
          const img = z === top || z === 0 ? image(sha) : images.get(sha);
          if (!img) continue;
          const a = toScreen(v, w, h, tx * span, ty * span);
          const b = toScreen(v, w, h, (tx + 1) * span, (ty + 1) * span);
          // Edges on whole device px: tiles meet without a hairline between them.
          const [sx, sy] = [Math.round(a.x * dpr), Math.round(a.y * dpr)];
          ctx.drawImage(img, sx, sy, Math.round(b.x * dpr) - sx, Math.round(b.y * dpr) - sy);
        }
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const ink = (strokes: readonly MapStroke[], color: string, alpha: number) => {
      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.globalAlpha = alpha;
      for (const s of strokes) {
        const pts = s.points.map(([x, y]) => toScreen(v, w, h, x, y));
        // Its width in the world, but never a band over what it marks once zoomed in.
        ctx.lineWidth = Math.max(2, Math.min(10, s.width * v.scale));
        ctx.beginPath();
        for (const [i, p] of pts.entries()) i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y);
        if (pts.length === 1) ctx.lineTo((pts[0]?.x ?? 0) + 0.01, pts[0]?.y ?? 0);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    };
    for (const n of props.notes)
      if (n.id !== props.selected?.id && ACTIVE.includes(n.status)) ink(n.strokes, STATUS[n.status][1], 0.55);
    const sel = props.selected;
    if (sel) {
      ink(sel.strokes, STATUS[sel.status][1], 0.9);
      const a = toScreen(v, w, h, sel.box.cx * TILE_SIZE, sel.box.cy * TILE_SIZE);
      const b = toScreen(v, w, h, (sel.box.cx + sel.box.w) * TILE_SIZE, (sel.box.cy + sel.box.h) * TILE_SIZE);
      ctx.setLineDash([6, 4]);
      ctx.lineWidth = 1;
      ctx.strokeStyle = '#ffffff';
      ctx.globalAlpha = 0.8;
      ctx.strokeRect(Math.round(a.x) + 0.5, Math.round(a.y) + 0.5, Math.round(b.x - a.x), Math.round(b.y - a.y));
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
      ctx.font = '600 12px "Geist Mono Variable", monospace';
      const label = `M${sel.id}`;
      ctx.fillStyle = 'rgba(0,0,0,0.7)';
      ctx.fillRect(Math.round(a.x), Math.round(a.y) - 18, ctx.measureText(label).width + 10, 18);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(label, Math.round(a.x) + 5, Math.round(a.y) - 5);
    }
    ink(props.draft, '#ff2bd6', 0.9);
    const now = drawing();
    if (now) ink([{ width: PEN / v.scale, points: now }], '#ff2bd6', 0.9);
  }

  /** World point under a pointer event. */
  const at = (e: PointerEvent) => {
    const r = canvas.getBoundingClientRect();
    const v = view() as View;
    return toWorld(v, r.width, r.height, e.clientX - r.left, e.clientY - r.top);
  };
  let pan: { sx: number; sy: number; v: View; moved: boolean } | null = null;
  /** Where the left button went down, and whether it has moved off it since (a click opens a note, a drag draws). */
  let press: { sx: number; sy: number; moved: boolean } | null = null;
  function down(e: PointerEvent) {
    const v = view();
    if (!v) return;
    canvas.setPointerCapture(e.pointerId);
    if (e.button === 0 && !space()) {
      const p = at(e);
      press = { sx: e.clientX, sy: e.clientY, moved: false };
      setDrawing([[p.x, p.y]]);
      return;
    }
    pan = { sx: e.clientX, sy: e.clientY, v, moved: false };
    setPanning(true);
  }
  function move(e: PointerEvent) {
    const p = at(e);
    setCursor(p);
    const now = drawing();
    if (now && press) {
      const v = view() as View;
      if (Math.hypot(e.clientX - press.sx, e.clientY - press.sy) > 3) press.moved = true;
      const [lx, ly] = now[now.length - 1] as [number, number];
      if (Math.hypot(p.x - lx, p.y - ly) * v.scale >= 2) setDrawing([...now, [p.x, p.y]]);
      return;
    }
    if (!pan) return;
    const [dx, dy] = [e.clientX - pan.sx, e.clientY - pan.sy];
    if (Math.hypot(dx, dy) > 3) pan.moved = true;
    setView({ ...pan.v, x: pan.v.x - dx / pan.v.scale, y: pan.v.y - dy / pan.v.scale });
  }
  function up(e: PointerEvent) {
    const now = drawing();
    const drew = press?.moved ?? false;
    press = null;
    if (now && drew) {
      setDrawing(null);
      const v = view() as View;
      const clamp = ([x, y]: [number, number]): [number, number] => [
        Math.round(Math.max(0, Math.min(m().width, x))),
        Math.round(Math.max(0, Math.min(m().height, y))),
      ];
      props.stroke({
        width: Math.round(Math.max(1, Math.min(MAP_NOTES.maxWidth, PEN / v.scale))),
        points: simplify(now, 0.75 / v.scale).map(clamp),
      });
      return;
    }
    setDrawing(null);
    const wasClick = now !== null || (pan !== null && !pan.moved);
    pan = null;
    setPanning(false);
    if (!wasClick || e.button !== 0) return;
    // A click opens the smallest open note round it.
    const p = at(e);
    const hit = props.notes
      .filter((n) => ACTIVE.includes(n.status) || n.id === props.selected?.id)
      .filter((n) => {
        const pad = 8 / (view() as View).scale;
        const [x0, y0] = [n.box.cx * TILE_SIZE - pad, n.box.cy * TILE_SIZE - pad];
        return (
          p.x >= x0 &&
          p.y >= y0 &&
          p.x <= x0 + n.box.w * TILE_SIZE + 2 * pad &&
          p.y <= y0 + n.box.h * TILE_SIZE + 2 * pad
        );
      })
      .sort((a, b) => a.box.w * a.box.h - b.box.w * b.box.h)[0];
    props.select(hit?.id ?? null);
  }

  const zoomBy = (factor: number) => {
    const v = view();
    const { w, h } = size();
    if (v) setView(zoomAt(v, w, h, w / 2, h / 2, factor, minScale()));
  };
  return (
    <div ref={box} class="absolute inset-0">
      <canvas
        ref={canvas}
        class="block size-full touch-none"
        style={{ cursor: panning() ? 'grabbing' : space() ? 'grab' : 'crosshair' }}
        onContextMenu={(e) => e.preventDefault()}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={() => {
          pan = null;
          press = null;
          setPanning(false);
          setDrawing(null);
        }}
        onPointerLeave={() => setCursor(null)}
      />
      <div class="bg-surface-elevated-bg/90 border-surface-elevated-border absolute top-3 right-3 flex flex-col overflow-hidden rounded-lg border">
        <IconButton label="Zoom in" onClick={() => zoomBy(2)}>
          <Icon name="plus" class="size-4" />
        </IconButton>
        <IconButton label="Zoom out" onClick={() => zoomBy(0.5)}>
          <Icon name="minus" class="size-4" />
        </IconButton>
        <IconButton
          label="A player’s screen (1)"
          onClick={() => {
            const v = view();
            if (v) setView(playerView(v.x, v.y, size().w, size().h));
          }}
        >
          <Icon name="monitor" class="size-4" />
        </IconButton>
        <IconButton label="The whole map (0)" onClick={fitWhole}>
          <Icon name="frame" class="size-4" />
        </IconButton>
      </div>
      <div class="text-fg-secondary pointer-events-none absolute top-3 left-3 flex items-center gap-3 rounded-md bg-black/60 px-2 py-1 font-mono text-xs tabular-nums">
        <Show when={cursor()} fallback={<span>{mapName(m().map)}</span>}>
          {(c) => (
            <span>
              cell {Math.floor(c().x / TILE_SIZE)}, {Math.floor(c().y / TILE_SIZE)}
            </span>
          )}
        </Show>
        <span title="Screen px a world px; a 1080p player sees ×4">
          ×{((view()?.scale ?? 0) * devicePixelRatio).toFixed(1)}
        </span>
        <span class="text-fg-muted">{m().version.slice(0, 7)}</span>
      </div>
      {/* The pen's buttons; while a note is being written its form says the rest. */}
      <Show when={!props.draft.length}>
        <div class="text-fg-secondary pointer-events-none absolute right-3 bottom-3 flex items-center gap-3 rounded-md bg-black/60 px-2 py-1.5 text-xs">
          <For
            each={[
              ['Left drag', 'draw'],
              ['Right drag', 'move'],
              ['Scroll', 'zoom'],
              ['Click a mark', 'open it'],
            ]}
          >
            {([key, does]) => (
              <span class="flex items-center gap-1.5">
                <kbd class="ld-kbd" data-size="sm">
                  {key}
                </kbd>
                {does}
              </span>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}
